// Adding a link on the web: the Depth API's fetch server extracts it (FETCH.md, SERVER.md §4); the reply is
// post-processed exactly like the phones do (IMAGES.md §1), chunked into parts, and the article syncs once ready.
import { account } from "../api/account";
import { api, errorBody, problem, problemMessage } from "../api/client";
import { chunkArticle } from "../narration/chunker";
import { isoNow, nowMs } from "../util";
import { uuid, type Article } from "./article";
import { postProcess } from "./images";
import { library } from "./library";
import { tagArticle } from "../tags/tagger";

export function normalizeURL(input: string): string | null {
  let s = input.trim();
  if (!s) return null;
  if (!/^[a-z]+:\/\//i.test(s)) s = "https://" + s;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
  } catch { return null; }
}

const BLOCKED = "The site asked for a sign-in or a check. Open your server browser from Depth on your phone (Settings → Fetching → Open my browser), sign in there, then Retry.";

export async function addLink(input: string, existingId?: string): Promise<Article | null> {
  const url = normalizeURL(input);
  if (!url) throw new Error("That doesn't look like a link.");
  if (!account.features.fetch) throw new Error("Fetching isn't available for this account.");
  const dup = library.list().find((a) => a.status === "ready" && a.meta.sourceURL === url);
  if (dup && !existingId) return dup;
  const id = existingId ?? uuid();
  const base: Article = { id, status: "fetching", url, meta: { title: url, sourceURL: url, addedAt: isoNow(), chunks: [] },
    metaUpdatedAt: 0, progress: { chunk: 0, offset: 0, finished: false }, progressUpdatedAt: 0, synced: false };
  library.store(base);
  const fail = (error: string) => { library.store({ ...base, status: "failed", error }); return null; };
  let res: Response;
  try {
    res = await api("/v1/fetch", { method: "POST", json: { url }, signal: AbortSignal.timeout(75_000) });
  } catch (e) {
    return fail(e instanceof DOMException && e.name === "TimeoutError" ? "Timed out after 75 s." : "Couldn't reach the server.");
  }
  const { json } = res.ok ? { json: await res.json().catch(() => null) } : await errorBody(res);
  if (!res.ok) {
    const p = problem(res.status, json);
    return fail(p ? problemMessage(p) : `The server couldn't fetch it (HTTP ${res.status}).`);
  }
  const status = json?.status;
  if (status === "paywall" || status === "login" || status === "captcha") return fail(`${json.detail ?? status}. ${BLOCKED}`);
  if (status !== "ok") return fail(`Couldn't get the article: ${status ?? "unexpected reply"}${json?.detail ? ": " + json.detail : ""}.`);
  const { paragraphs, quotes, images } = postProcess(json.paragraphs ?? [], json.images);
  if (!paragraphs.length) return fail("No article text found.");
  const now = isoNow();
  const ready: Article = {
    ...base, status: "ready", error: undefined, metaUpdatedAt: nowMs(), progressUpdatedAt: 0,
    meta: {
      sourceURL: json.finalURL || url, title: json.title || url, siteName: json.siteName ?? null, author: json.byline ?? null,
      excerpt: json.excerpt ?? null, text: paragraphs.join("\n\n"), addedAt: base.meta.addedAt, readyAt: now,
      chunks: chunkArticle(paragraphs, null).map((text, index) => ({ index, text })),
      quoteParagraphs: quotes, images,
    },
  };
  library.store(ready);
  library.onLocalChange(id, true, true);
  tagArticle(id).catch(() => {});
  return ready;
}
